import { leaveBalanceExceededFrom } from "../utils/leaveBalanceOverride";
import { API_URL } from "../config";
import type {
  AuthTokenResponse,
  LogoutResponseDto,
  SmsInitEndRequest,
  SmsInitResponse,
} from "../types/auth";
import type { LeaveCalcResponseDto, LeaveRequest } from "../types/leave";
import type { CreateManualLocationDto } from "../types/daily-shifts";
import type { LeaveTypeCreate } from "../types/leave-type";
import type { IPostUser } from "../types/user";
import type { TransferUnitDto, TransferUserDto } from "../types/transfer";
import type { CreateUnitDto, UnitResponse } from "../types/unit";
import type { IOvertimeItemWrapper, IOvertimeRequest } from "../types/overtime";
import type {
  CreateLeaveApprovalProcessDto,
  LeaveApprovalProcess,
} from "../types/leave-approval-process";
import type {
  CreateOvertimeApprovalProcessDto,
  OvertimeApprovalProcess,
} from "../types/overtime-approval-process";
import type {
  CalendarHolidaysCreate,
  CalendarHolidayResponse,
} from "../types/calendar-holidays";
import type {
  CreateAppVersionDto,
  CheckVersionDto,
  VersionResponseDto,
  AppVersionResponse,
} from "../types/app-version";
import type {
  AnnouncementCreateRequest,
  AnnouncementByIdResponse,
  AnnouncementSmsPreviewRequest,
  AnnouncementSmsPreviewResponse,
} from "../types/announcement";
import type { CreateEmploymentDto } from "../types/employement";
import type { createGateType, gateResponse } from "../types/gate";
import type { createFactoryType, factoryResponse } from "../types/factory";
import type { Realm, RealmCreate } from "../types/realm";
import type { PayrollSyncParams, PayrollSyncResponse } from "../types/payroll";
import type {
  SingleWorkCalendarResponse,
  WorkCalendarCreate,
} from "../types/work-calendar";
import type { IGuestByIDResponse } from "../types/guest";
import type { CreateEntranceDto, EntranceResponse } from "../types/entrance";
import type {
  FeatureFlagCreate,
  FeatureFlagResponse,
} from "../types/feature-flag";

export const initSms = async (
  phoneNumber: string,
): Promise<SmsInitResponse> => {
  const res = await fetch(API_URL + `v1/auth/sms/init`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ phoneNumber }),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const rejectLeave = async (
  leaveId: string,
  data: { reason?: string; status: string },
  token: string,
) => {
  const res = await fetch(API_URL + `v1/leave/${leaveId}/approve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    throw new Error(`Failed to reject leave: ${res.statusText}`);
  }

  return res.json();
};

export const approveLeave = async (
  leaveId: string,
  data: { reason?: string; status: string },
  token: string,
) => {
  const res = await fetch(API_URL + `v1/leave/${leaveId}/approve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    throw new Error(`Failed to approve leave: ${res.statusText}`);
  }

  return res.json();
};

export const initEndSms = async (
  body: SmsInitEndRequest,
): Promise<AuthTokenResponse> => {
  const res = await fetch(API_URL + `v1/auth/sms/end`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...body,
    }),
  });

  if (!res.ok) {
    throw new Error(`failed: ${res.statusText}`);
  }

  return await res.json();
};

export const refreshToken = async (
  token: string,
): Promise<AuthTokenResponse> => {
  const res = await fetch(API_URL + `v1/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken: token }),
  });

  if (!res.ok) {
    throw new Error(`Refresh failed: ${res.statusText}`);
  }

  return await res.json();
};

export const postUser = async (data: IPostUser, token: string) => {
  const res = await fetch(API_URL + `v1/user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postGates = async (
  data: createGateType,
  token: string,
): Promise<gateResponse> => {
  const res = await fetch(API_URL + `v1/gate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postFactory = async (
  data: createFactoryType,
  token: string,
): Promise<factoryResponse> => {
  const res = await fetch(API_URL + `v1/factory`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postWorkCalendar = async (
  data: WorkCalendarCreate,
  token: string,
): Promise<SingleWorkCalendarResponse> => {
  const res = await fetch(API_URL + `v1/work-calendar`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postCalendarHoliday = async (
  data: CalendarHolidaysCreate,
  token: string,
): Promise<CalendarHolidayResponse> => {
  const res = await fetch(API_URL + `v1/calendar-holidays`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const resetApprovalProcessForEmployment = async (
  employmentId: string,
  token: string,
) => {
  const res = await fetch(
    API_URL + `v1/employment/resetapprovalprocess/${employmentId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!res.ok) {
    throw new Error(`Failed to reset approval process: ${res.statusText}`);
  }

  return res.json();
};

export const resetOvertimeProcessForEmployment = async (
  employmentId: string,
  token: string,
) => {
  const res = await fetch(
    API_URL + `v1/employment/resetovertimeprocess/${employmentId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!res.ok) {
    throw new Error(`Failed to reset overtime process: ${res.statusText}`);
  }

  return res.json();
};

export const postLeave = async (data: LeaveRequest, token: string) => {
  const res = await fetch(API_URL + `v1/leave`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw leaveBalanceExceededFrom(resp) ?? new Error(resp.message);
  }

  return resp;
};

export const postLeaveType = async (data: LeaveTypeCreate, token: string) => {
  const res = await fetch(API_URL + `v1/leave-type`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const createUnit = async (
  data: CreateUnitDto,
  token: string,
): Promise<UnitResponse> => {
  const res = await fetch(API_URL + `v1/unit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const createRealm = async (
  data: RealmCreate,
  token: string,
): Promise<Realm> => {
  const res = await fetch(API_URL + `v1/realm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postLeaveApprovalProcess = async (
  data: CreateLeaveApprovalProcessDto,
  token: string,
): Promise<LeaveApprovalProcess> => {
  const res = await fetch(API_URL + `v1/leave-approval-process`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postOvertimeApprovalProcess = async (
  data: CreateOvertimeApprovalProcessDto,
  token: string,
): Promise<OvertimeApprovalProcess> => {
  const res = await fetch(API_URL + `v1/overtime-approval-process`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postOvertime = async (
  data: IOvertimeRequest,
  token: string,
): Promise<IOvertimeItemWrapper> => {
  const res = await fetch(API_URL + `v1/overtime`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message || "Failed to create overtime");
  }

  return resp;
};

export const postAnnouncement = async (
  data: AnnouncementCreateRequest,
  token: string,
): Promise<AnnouncementByIdResponse> => {
  const form = new FormData();
  form.append("title", data.title);
  form.append("category", data.category);
  if (data.featured) form.append("featured", "true");
  form.append("body", data.body);
  form.append("type", data.type);
  form.append("units", JSON.stringify(data.units ?? []));
  if (data.audienceFilter) form.append("audienceFilter", data.audienceFilter);
  if (data.realmId) form.append("realmId", data.realmId);
  if (data.cover) form.append("cover", data.cover);
  if (data.survey) form.append("survey", JSON.stringify(data.survey));
  data.attachments?.forEach((file) => form.append("attachments", file));
  const res = await fetch(API_URL + `v1/announcement`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message || "Failed to create announcement");
  }

  return resp;
};

export const generateAnnouncementCover = async (
  prompt: string,
  token: string,
) => {
  const res = await fetch(API_URL + "v1/announcement/generate-cover", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Görsel üretilemedi");
  return result as { mimeType: string; data: string };
};

export const postAnnouncementSmsPreview = async (
  data: AnnouncementSmsPreviewRequest,
  token: string,
): Promise<AnnouncementSmsPreviewResponse> => {
  const res = await fetch(API_URL + `v1/announcement/sms-preview`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message || "Failed to preview SMS recipients");
  }

  return resp;
};

export const postEmployment = async (
  data: CreateEmploymentDto,
  token: string,
) => {
  const res = await fetch(API_URL + `v1/employment`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message || "Failed to create employment");
  }

  return resp;
};

export const getUserPhotoUploadUrl = async (
  token: string,
): Promise<{ url: string; id: string }> => {
  const res = await fetch(API_URL + `v1/user/upload-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const resp = await res.json();
  if (!res.ok) {
    throw new Error(resp.message || "Failed to get upload url");
  }
  return resp;
};

export const confirmUserPhotoUpload = async (
  token: string,
  params: { id: string; userId: string },
): Promise<{
  success: boolean;
  message: string;
  photoUrl: string;
  targetUserId: string;
}> => {
  const res = await fetch(API_URL + `v1/user/confirm-upload`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  const resp = await res.json();
  if (!res.ok) {
    throw new Error(resp.message || "Failed to confirm upload");
  }
  return resp;
};

export const confirmRealmPhotoUpload = async (
  token: string,
  params: { id: string; userId: string },
): Promise<{
  success: boolean;
  message: string;
  photoUrl: string;
  targetRealmId: string;
}> => {
  const res = await fetch(API_URL + `v1/realm/confirm-upload`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  const resp = await res.json();
  if (!res.ok) {
    throw new Error(resp.message || "Failed to confirm upload");
  }
  return resp;
};

export const deleteUserPhoto = async (
  token: string,
  params: { id: string; userId: string },
): Promise<void> => {
  const res = await fetch(API_URL + `v1/user/delete-photo`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to delete photo");
  }
};

export const deleteRealmPhoto = async (
  token: string,
  params: { id: string; userId: string },
): Promise<void> => {
  const res = await fetch(API_URL + `v1/realm/delete-photo`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to delete photo");
  }
};

export const postCalcLeave = async (
  token: string,
  params: { startDate: string; endDate: string; unitId: string },
): Promise<LeaveCalcResponseDto> => {
  const res = await fetch(API_URL + `v1/leave/calculate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to calculate leave");
  }

  return res.json();
};

export const syncPayroll = async (
  token: string,
  body: PayrollSyncParams,
): Promise<PayrollSyncResponse> => {
  const res = await fetch(API_URL + `v1/payroll/sync`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to calculate leave");
  }

  return res.json();
};

export const createAppVersion = async (
  data: CreateAppVersionDto,
  token: string,
): Promise<AppVersionResponse> => {
  const res = await fetch(API_URL + `v1/app-version`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to create app version");
  }

  return res.json();
};

export const checkVersion = async (
  data: CheckVersionDto,
): Promise<VersionResponseDto> => {
  const res = await fetch(API_URL + `v1/app-version/check`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to check version");
  }

  return res.json();
};

export const logOut = async (token: string): Promise<LogoutResponseDto> => {
  const res = await fetch(API_URL + `v1/auth/logout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || "Failed to logout");
  }

  return res.json();
};

export const transferUser = async (data: TransferUserDto, token: string) => {
  const res = await fetch(API_URL + `v1/transfer/user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const transferUnit = async (data: TransferUnitDto, token: string) => {
  const res = await fetch(API_URL + `v1/transfer/unit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const createGuest = async (
  data: {
    name: string;
    surname: string;
    tc?: string;
    phone?: string;
  },
  token: string,
): Promise<IGuestByIDResponse> => {
  const res = await fetch(API_URL + `v1/guest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const createEntrance = async (
  data: CreateEntranceDto,
  token: string,
): Promise<EntranceResponse> => {
  const res = await fetch(API_URL + `v1/entrance`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

export const postFeatureFlag = async (
  data: FeatureFlagCreate,
  token: string,
): Promise<FeatureFlagResponse> => {
  const res = await fetch(API_URL + `v1/feature-flag`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await res.json();

  if (!res.ok) {
    throw new Error(resp.message);
  }

  return resp;
};

/**
 * Cihazdan vardiya başlatmayı unutan çalışan için kaydı elle açar.
 * Giriş saati zorunludur; çıkış boş bırakılırsa vardiya açık kalır.
 */
export const createManualLocation = async (
  data: CreateManualLocationDto,
  token: string,
): Promise<unknown> => {
  const res = await fetch(API_URL + `v1/locations/manual`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    // Backend hata mesajını kullanıcıya gösterebilmek için gövdeyi okuyoruz
    const body = await res.json().catch(() => null);
    throw new Error(
      body?.message || `Vardiya oluşturulamadı: ${res.statusText}`,
    );
  }

  return res.json();
};
